/**
 * /confirm-email — redeems the email confirmation token.
 *
 * The auth service sends the confirmation link to the project's `siteUrl`,
 * which is Friday, so the link lands here as `/confirm-email?token=…`. The
 * account exists from the moment it was created, but no session was issued
 * for it — so this page confirms the address and sends the user to sign in.
 * A sign-up that started from an invite link goes back to that invite.
 */

import { useEffect, useState } from 'react'
import { Link, useSearch } from '@tanstack/react-router'
import { useAuth } from '@/features/auth/authContext'
import { ApiError } from '@/lib/api'
import { pendingInvitePath } from '@/lib/pendingInvite'
import { FridayMark } from '@/components/branding/FridayMark'
import { useTheme } from '@/features/theme/ThemeProvider'

type State = 'working' | 'verified' | 'failed'

export function ConfirmEmailPage() {
  const { confirmEmail } = useAuth()
  const search = useSearch({ from: '/confirm-email' }) as { token?: string }
  const token = search.token ?? ''
  const [state, setState] = useState<State>(token ? 'working' : 'failed')
  const [error, setError] = useState<string | null>(null)
  const { theme } = useTheme()
  const [invitePath] = useState(pendingInvitePath)

  useEffect(() => {
    if (!token) {
      setError('This confirmation link is missing its token.')
      return
    }
    let cancelled = false
    confirmEmail(token)
      .then(() => {
        if (!cancelled) setState('verified')
      })
      .catch((e) => {
        if (cancelled) return
        setError(e instanceof ApiError ? e.message : 'Could not confirm the address. Please try again.')
        setState('failed')
      })
    return () => {
      cancelled = true
    }
  }, [confirmEmail, token])

  return (
    <div className="flex h-full items-center justify-center bg-app px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <FridayMark size={64} className="mx-auto mb-3" />
          <h1 className="text-2xl font-semibold tracking-tight">
            {state === 'verified' ? 'Email confirmed' : state === 'working' ? 'Confirming…' : 'Confirmation failed'}
          </h1>
          <p className="mt-1 text-sm text-fg-muted">
            {state === 'verified'
              ? 'Your address is confirmed. Sign in to get started.'
              : state === 'working'
                ? 'One moment while we check the link.'
                : 'We could not use that confirmation link.'}
          </p>
        </div>

        <div
          className="space-y-4 border border-border bg-elevated p-6 shadow-lg"
          style={{ borderRadius: 'var(--radius-lg)' }}
        >
          {state === 'verified' && (
            <>
              <p className="text-sm text-fg-muted">
                {invitePath
                  ? 'Your account is ready. Sign in to accept your invite.'
                  : 'Your account is ready. Sign in with your email and password.'}
              </p>
              <Link to="/login" search={{ next: invitePath }} className="btn-primary w-full justify-center">
                Sign in
              </Link>
            </>
          )}

          {state === 'working' && (
            <p className="text-sm text-fg-muted">Checking the confirmation token…</p>
          )}

          {state === 'failed' && (
            <>
              <p className="text-sm text-fg-muted">{error ?? 'That confirmation link is invalid or has expired.'}</p>
              <Link to="/signup" search={{ next: invitePath }} className="btn-primary w-full justify-center">
                Create an account
              </Link>
            </>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-fg-muted">
          <Link to="/login" search={{ next: invitePath }} className="font-medium text-accent hover:underline">
            Back to sign in
          </Link>
          <br />
          <span className="opacity-60">Theme · {theme.name}</span>
        </p>
      </div>
    </div>
  )
}
